import { Box, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import interfaceBridge from 'utils/interfaceBridge';
import MapHelper from 'utils/shared/MapHelper';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'hooks/useRootStore';
import { pcMenuCategoryNames } from 'constants/frontend/app/pcMenuCategoryNames';

type CategoryConfigT = {
  /** 내부 라우트 path */
  path?: string;
  /** 외부 링크 */
  externalUrl?: string;
  /** 권한 조건 – 필요하면 타입 더 추가 가능 */
  requireRole?: 'financialManager' | 'manager';
};

const categoryConfigRecord: Record<string, CategoryConfigT> = {
  management: {
    path: '/teacherSide/management/student/list',
  },
  textbook: {
    path: '/teacherSide/textbook/list',
    requireRole: 'manager',
  },
  bill: {
    path: '/teacherSide/bill/group/list',
    requireRole: 'financialManager',
  },
  post: {
    path: '/teacherSide/post/generalBoard',
  },
  attendance: {
    path: '/teacherSide/attendance/student/ofToday',
  },
  homeworkAndSchedule: {
    path: '/teacherSide/homework/list',
  },
  chat: {
    externalUrl: 'https://inapp.anotherclass.co.kr/chatApp/',
  },
  counsel: {
    path: '/teacherSide/counsel/log',
  },
  report: {
    path: '/teacherSide/lessonReport/group/list',
  },
  message: {
    path: '/teacherSide/message/send',
    requireRole: 'manager',
  },
  // 설정이 없는 카테고리는 default ("준비중입니다") 처리
};

const NavbarCatgories = observer(() => {
  console.log('categories');

  const navigate = useNavigate();

  const userStore = useRootStore().userStore;

  const alertNoPermission = async (categoryName?: string) =>
    interfaceBridge.customAlert(categoryName || '', '해당 메뉴에 접근할 권한이 없습니다.', '닫기');

  const alertNotReady = async (categoryName?: string) =>
    interfaceBridge.customAlert(categoryName || '', '해당 메뉴는 준비중입니다.', '닫기');

  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), {
    noSsr: true,
  });

  const isNewTabClick = (e: React.MouseEvent) =>
    e.metaKey || // ⌘ (mac)
    e.ctrlKey || // Ctrl (win)
    e.shiftKey || // Shift+Click (보통 새 창)
    e.button === 1; // 휠 클릭

  const openInternal = (path: string, newTab: boolean) => {
    if (newTab) {
      const url = `${window.location.origin}${path}`;
      window.open(url, '_blank', 'noopener');
    } else {
      navigate(path);
    }
  };

  const openExternal = (url: string, newTab: boolean) => {
    window.open(url, newTab ? '_blank' : '_self', 'noopener');
  };

  const handleClickCategory = (
    e: React.MouseEvent<HTMLDivElement>,
    categoryName: string,
    categoryKey: string,
  ) => {
    const config = categoryConfigRecord[categoryKey];

    // 설정이 없으면 "준비중입니다"
    if (!config) {
      void alertNotReady(categoryName);
      return;
    }

    // 권한 체크
    if (config.requireRole === 'financialManager' && !userStore.isFinancialManager) {
      void alertNoPermission(categoryName);
      return;
    }
    if (config.requireRole === 'manager' && !userStore.isManager) {
      void alertNoPermission(categoryName);
      return;
    }

    const newTab = isNewTabClick(e);

    if (config.externalUrl) {
      openExternal(config.externalUrl, newTab);
      return;
    }

    if (config.path) {
      openInternal(config.path, newTab);
      return;
    }

    // path도 externalUrl도 없으면 역시 준비중 처리
    void alertNotReady(categoryName);
  };

  return (
    <Stack direction="row" spacing={lgUp ? 2 : 1} overflow="hidden">
      {MapHelper.toValueNameArray(pcMenuCategoryNames).map((category, i) => (
        <Box
          sx={{
            position: 'relative',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          key={i}
          onClick={(e) => handleClickCategory(e, category.name, category.value)}
        >
          <Typography variant={lgUp ? 'h6' : 'subtitle1'} sx={{ whiteSpace: 'nowrap' }}>
            {category.name}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
});

export default NavbarCatgories;
